import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText, FiDownload, FiMail, FiPhone, FiAlertTriangle, FiPaperclip, FiMap, FiPlus, FiX, FiMessageCircle, FiSearch, FiRefreshCw } from 'react-icons/fi';
import './TransfertList.css';
import { extractOwnerId } from './utils/auth';
import { fetchWithAuthFallback } from './utils/api';
import { normalizeTransferDossiers } from './utils/transferts';

const API_BASE_URL = 'https://admin.cgiibnn-esursi.cd/api/candidat';
const ADMIN_LIST_KEYS = ['results', 'data', 'candidats', 'candidates', 'items'];
const ADMIN_DOSSIER_KEYS = ['dossiers', 'demandes', 'transferts', 'transfert_demandes', 'demandes_transfert'];

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const getFirstObjectValue = (source, keys) => {
  if (!isPlainObject(source)) return null;
  for (const key of keys) {
    if (isPlainObject(source[key])) {
      return source[key];
    }
  }
  return null;
};

const getFirstArrayValue = (source, keys) => {
  if (!isPlainObject(source)) return null;
  for (const key of keys) {
    if (Array.isArray(source[key])) {
      return source[key];
    }
  }
  return null;
};

const getAdminPayloadItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  for (const key of ADMIN_LIST_KEYS) {
    if (Array.isArray(payload[key])) {
      return payload[key];
    }
  }

  for (const key of ADMIN_LIST_KEYS) {
    if (isPlainObject(payload[key])) {
      const nestedItems = getAdminPayloadItems(payload[key]);
      if (nestedItems.length > 0) {
        return nestedItems;
      }
    }
  }

  return [];
};

const getCandidateFromRecord = (record, dossier = {}) => (
  getFirstObjectValue(dossier, ['candidat', 'candidate', 'user', 'candidat_info']) ||
  getFirstObjectValue(record, ['candidat', 'candidate', 'user', 'candidat_info']) ||
  getFirstObjectValue(record?.data, ['candidat', 'candidate', 'user', 'candidat_info']) ||
  getFirstObjectValue(dossier?.data, ['candidat', 'candidate', 'user', 'candidat_info']) ||
  record ||
  {}
);

const getDossiersFromAdminRecord = (record) => {
  const directDossiers = getFirstArrayValue(record, ADMIN_DOSSIER_KEYS);
  if (directDossiers) return directDossiers;

  const dataDossiers = getFirstArrayValue(record?.data, ADMIN_DOSSIER_KEYS);
  if (dataDossiers) return dataDossiers;

  return null;
};

const formatDateValue = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const isDateLikeValue = (key, value) => {
  if (!value || typeof value !== 'string') return false;
  const normalizedKey = String(key).toLowerCase();
  return normalizedKey.includes('date') || /^\d{4}-\d{2}-\d{2}/.test(value);
};

const formatFieldLabel = (key) => String(key || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isUrlValue = (value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim());

const isFileValue = (key, value) => {
  if (!isUrlValue(value)) return false;
  const normalizedKey = String(key).toLowerCase();
  return [
    'file',
    'fichier',
    'document',
    'piece',
    'preuve',
    'attestation',
    'acte',
    'pv',
    'carte',
    'lettre',
    'formulaire',
    'certificat',
  ].some((fragment) => normalizedKey.includes(fragment));
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRangeFromFilter = (filter, customStart, customEnd) => {
  if (!filter) return null;

  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);

  if (filter === 'today') {
    return { start: toDateInputValue(start), end: toDateInputValue(end) };
  }

  if (filter === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
    return { start: toDateInputValue(start), end: toDateInputValue(end) };
  }

  const dayOfWeek = start.getDay() || 7;
  if (filter === 'this_week') {
    start.setDate(start.getDate() - dayOfWeek + 1);
    end.setDate(start.getDate() + 6);
    return { start: toDateInputValue(start), end: toDateInputValue(end) };
  }

  if (filter === 'last_week') {
    start.setDate(start.getDate() - dayOfWeek - 6);
    end.setTime(start.getTime());
    end.setDate(start.getDate() + 6);
    return { start: toDateInputValue(start), end: toDateInputValue(end) };
  }

  if (filter === 'this_month') {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: toDateInputValue(monthStart), end: toDateInputValue(monthEnd) };
  }

  if (filter === 'last_month') {
    const monthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: toDateInputValue(monthStart), end: toDateInputValue(monthEnd) };
  }

  if (filter === 'custom' && customStart && customEnd) {
    return { start: customStart, end: customEnd };
  }

  return null;
};

const getDateOnlyValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return toDateInputValue(date);
};

const getCategoryLabel = (value) => {
  if (value === 'office' || value === 'transfert_office') {
    return "Transfert d'office";
  }
  return String(value || '');
};

const normalizeAdminCategoryValue = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, '_')
  .replace(/-/g, '_');

const getAdminRecordCategoryValues = (record) => {
  const transfert = record?.transfert || {};
  const rawValues = [
    transfert.categorie,
    transfert.form_type,
    transfert.type_demande,
    transfert.raw_demande?.categorie,
    transfert.raw_demande?.form_type,
    transfert.raw_demande?.type_demande,
  ];

  if (transfert.is_office_transfer) {
    rawValues.push('office', 'transfert_office');
  }

  return rawValues
    .filter(Boolean)
    .map(normalizeAdminCategoryValue);
};

const formatDisplayValue = (key, value) => {
  if (String(key).toLowerCase().includes('categorie')) {
    return getCategoryLabel(value);
  }
  if (value === 'office' || value === 'transfert_office') {
    return "Transfert d'office";
  }
  return String(value);
};

const TransfertList = ({
  user,
  token,
  onLogout,
  onCreateNew,
  onEditTransfert,
  onShowGuide,
  onOpenChat,
  chatUnreadCount = 0,
  canAccessChat = false,
  onTransferDossiersChange,
}) => {
  const isAdmin = user?.est_admin_transfert === true;
  const [transferts, setTransferts] = useState([]);
  const [adminCandidates, setAdminCandidates] = useState([]);
  const [selectedAdminRecord, setSelectedAdminRecord] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminStatusFilter, setAdminStatusFilter] = useState('');
  const [adminCategoryFilter, setAdminCategoryFilter] = useState('');
  const [adminTypeFilter, setAdminTypeFilter] = useState('');
  const [adminOrdering, setAdminOrdering] = useState('-date');
  const [adminDateFilter, setAdminDateFilter] = useState('');
  const [adminDateStart, setAdminDateStart] = useState('');
  const [adminDateEnd, setAdminDateEnd] = useState('');
  const [adminMatricule, setAdminMatricule] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminTelephone, setAdminTelephone] = useState('');
  const [adminUserId, setAdminUserId] = useState('');
  const [adminExternalUserId, setAdminExternalUserId] = useState('');
  const [adminPage, setAdminPage] = useState(1);
  const [adminPageSize] = useState(10);
  const [adminCount, setAdminCount] = useState(0);
  const [changingStatus, setChangingStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedTransfert, setSelectedTransfert] = useState(null);
  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [officeNumero, setOfficeNumero] = useState('');
  const [loadingOffice, setLoadingOffice] = useState(false);
  const [officeData, setOfficeData] = useState(null);

  const fetchTransferts = useCallback(async () => {
    if (isAdmin) {
      onTransferDossiersChange?.(true);
      return;
    }

    const ownerId = extractOwnerId(user);

    if (!ownerId) {
      setLoading(false);
      onTransferDossiersChange?.(false);
      setMessage({
        type: 'error',
        text: 'Identifiant utilisateur introuvable.',
      });
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    try {
      setLoading(true);
      const response = await fetchWithAuthFallback(`https://admin.cgiibnn-esursi.cd/api/candidat/transfert/demandes/?current_candidat_id=${ownerId}`, {
        method: 'GET',
        headers,
      }, token);
      if (response.ok) {
        const result = await response.json();
        const transfertItems = normalizeTransferDossiers(result);
        setTransferts(transfertItems);
        setSelectedTransfert((current) => {
          if (current && transfertItems.some((transfert) => transfert.list_id === current.list_id)) {
            return current;
          }
          return transfertItems[0] || null;
        });
        onTransferDossiersChange?.(transfertItems.length > 0);
        setMessage({ type: '', text: '' });
      } else {
        onTransferDossiersChange?.(false);
        let errorInfo = 'Impossible de charger les transferts';
        try {
          const error = await response.json();
          errorInfo = error.message || error.detail || errorInfo;
        } catch (err) {
          // Ignorer si le body n'est pas JSON
        }

        setMessage({
          type: 'error',
          text: response.status === 401
            ? "Session expirée ou incomplète : déconnectez-vous puis reconnectez-vous pour afficher vos dossiers."
            : `Erreur serveur (${response.status}) : ${errorInfo}`,
        });
      }
    } catch (error) {
      onTransferDossiersChange?.(false);
      setMessage({
        type: 'error',
        text: `Erreur réseau : ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, token, user, onTransferDossiersChange]);

  const normalizeAdminRecord = useCallback((record, dossierRecord = null) => {
    const demande = dossierRecord && typeof dossierRecord === 'object' ? dossierRecord : record;
    const data = demande.data && typeof demande.data === 'object' ? demande.data : {};
    const candidate = getCandidateFromRecord(record, demande);
    const typeDemande = demande.type_demande || data.type_demande;
    const isOffice = typeDemande === 'transfert_office' || data.is_office_transfer === true || demande.categorie === 'office';
    const demandeDate = isOffice
      ? (data.date_soumission || demande.date_soumission || demande.date)
      : (data.date_creation || data.date_modification || demande.date_creation || demande.date_modification || demande.date);
    const transfert = {
      ...demande,
      ...data,
      id: data.id || demande.id,
      demande_id: demande.id,
      numero_demande: demande.numero_demande || data.numero_demande,
      type_demande: typeDemande,
      categorie: demande.categorie || data.categorie || data.form_type,
      form_type: data.form_type || demande.form_type || demande.categorie,
      statut: demande.statut || data.statut,
      status: demande.status || data.status || demande.statut || data.statut,
      date_demande: demandeDate,
      is_office_transfer: isOffice,
      raw_demande: demande,
    };
    const candidateName = candidate.nom_complet ||
      [candidate.nom, candidate.postnom, candidate.prenom].filter(Boolean).join(' ') ||
      candidate.email ||
      transfert.nom_complet ||
      [transfert.nom, transfert.postnom, transfert.prenom].filter(Boolean).join(' ') ||
      transfert.email ||
      'Candidat non renseigné';
    const demandeId = demande.id || transfert.id || demande.transfert_id || record.id;

    return {
      id: `${transfert.type_demande || 'demande'}-${demandeId}`,
      candidate,
      transfert,
      raw: record,
      candidateName,
      transfertId: transfert.numero_demande || transfert.id || demande.transfert_id || demande.id || record.id,
      statusChangeId: transfert.id || demande.transfert_id || demande.id || record.id,
    };
  }, []);

  const normalizeAdminRecords = useCallback((records) => records.flatMap((record) => {
    const dossiers = getDossiersFromAdminRecord(record);
    if (Array.isArray(dossiers) && dossiers.length > 0) {
      return dossiers.map((dossier) => normalizeAdminRecord(record, dossier));
    }
    return [normalizeAdminRecord(record)];
  }), [normalizeAdminRecord]);

  const handleAdminFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setAdminPage(1);
  };

  const filterAdminRecordsByDate = useCallback((records) => {
    const range = getDateRangeFromFilter(adminDateFilter, adminDateStart, adminDateEnd);
    if (!range) return records;

    return records.filter((record) => {
      const dateValue = getDateOnlyValue(record?.transfert?.date_demande);
      return dateValue && dateValue >= range.start && dateValue <= range.end;
    });
  }, [adminDateEnd, adminDateFilter, adminDateStart]);

  const fetchAdminCandidates = useCallback(async () => {
    if (!isAdmin) return;

    const currentCandidatId = extractOwnerId(user);
    if (!currentCandidatId) {
      setLoading(false);
      setMessage({
        type: 'error',
        text: 'Identifiant candidat introuvable pour charger les dossiers.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    const params = new URLSearchParams({
      current_candidat_id: String(currentCandidatId),
      page: String(adminPage),
      page_size: String(adminPageSize),
    });

    if (adminSearch.trim()) {
      params.set('search', adminSearch.trim());
    }

    if (adminStatusFilter) {
      params.set('statut', adminStatusFilter);
    }

    if (adminCategoryFilter) {
      params.set('categorie', adminCategoryFilter);
      if (adminCategoryFilter !== 'office') {
        params.set('form_type', adminCategoryFilter);
      }
    }

    if (adminTypeFilter) {
      params.set('type_demande', adminTypeFilter);
    }

    if (adminOrdering) {
      params.set('ordering', adminOrdering);
    }

    if (adminMatricule.trim()) {
      params.set('matricule', adminMatricule.trim());
    }

    if (adminEmail.trim()) {
      params.set('email', adminEmail.trim());
    }

    if (adminTelephone.trim()) {
      params.set('telephone', adminTelephone.trim());
    }

    if (adminUserId.trim()) {
      params.set('user_id', adminUserId.trim());
    }

    if (adminExternalUserId.trim()) {
      params.set('external_user_id', adminExternalUserId.trim());
    }

    const dateRange = getDateRangeFromFilter(adminDateFilter, adminDateStart, adminDateEnd);
    if (dateRange) {
      params.set('date_debut', dateRange.start);
      params.set('date_fin', dateRange.end);
    }

    try {
      const response = await fetchWithAuthFallback(`${API_BASE_URL}/candidats-transferts/?${params.toString()}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }, token);

      if (response.ok) {
        const result = await response.json();
        const rawResults = getAdminPayloadItems(result);
        const normalized = filterAdminRecordsByDate(normalizeAdminRecords(rawResults))
          .filter((record) => {
            if (!adminCategoryFilter) return true;
            const selectedCategory = normalizeAdminCategoryValue(adminCategoryFilter);
            return getAdminRecordCategoryValues(record).includes(selectedCategory);
          });
        setAdminCandidates(normalized);
        setAdminCount(Number(result.count || normalized.length));
        setSelectedAdminRecord((current) => {
          if (!current) return normalized[0] || null;
          return normalized.find((record) => record.id === current.id) || normalized[0] || null;
        });
      } else {
        let errorInfo = 'Impossible de charger les candidats';
        try {
          const error = await response.json();
          errorInfo = error.message || error.detail || error.error || errorInfo;
        } catch (err) {
          // Ignorer si le body n'est pas JSON
        }
        setMessage({ type: 'error', text: `Erreur serveur (${response.status}) : ${errorInfo}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Erreur réseau : ${error.message}` });
    } finally {
      setLoading(false);
    }
  }, [
    adminCategoryFilter,
    adminDateEnd,
    adminDateFilter,
    adminDateStart,
    adminEmail,
    adminExternalUserId,
    adminMatricule,
    adminOrdering,
    adminPage,
    adminPageSize,
    adminSearch,
    adminStatusFilter,
    adminTelephone,
    adminTypeFilter,
    adminUserId,
    filterAdminRecordsByDate,
    isAdmin,
    normalizeAdminRecords,
    token,
    user,
  ]);

  // Récupérer la liste des transferts
  useEffect(() => {
    if (isAdmin) {
      fetchAdminCandidates();
    } else {
      fetchTransferts();
    }
  }, [fetchAdminCandidates, fetchTransferts, isAdmin]);

  const officeFileKeys = [
    'piece_identite',
    'carte_service',
    'acte_affectation',
    'acte_nomination',
    'acte_mariage',
    'preuve_emploi',
    'coordonnees_etablissement_transfert',
    'preuve_nouvelle_residence',
    'preuve_activite_ou_traitement',
  ];

  const isOfficeTransfert = (transfert) => (
    transfert?.is_office_transfer === true ||
    transfert?.type_demande === 'transfert_office' ||
    transfert?.categorie === 'office'
  );

  const getMissingFiles = (transfert) => {
    if (isOfficeTransfert(transfert)) {
      return officeFileKeys.filter(file => !transfert[file]);
    }

    let requiredFiles = [
      'piece_identite',
      'carte_service',
      'lettre_transfert',
      'formulaire_transfert_signe',
      'pv_origine',
      'pv_accueil',
      'cadre_organique_accueil',
      'attestation_effectifs_enseignants',
      // 'preuve_paiement',
      'preuve_charge_horaire',
      'pv_attribution_charge',
      'acte_nomination',
      'acte_affectation',
    ];

    // Exclure les fichiers non nécessaires selon le type
    if (transfert.form_type === 'enseignants') {
      requiredFiles = requiredFiles.filter(file => file !== 'attestation_effectifs_chercheurs');
    }

    if (transfert.form_type === 'chercheurs') {
      requiredFiles = requiredFiles.filter(file => file !== 'attestation_effectifs_chercheurs');
    }

    if (transfert.form_type === 'pato') {
      requiredFiles = requiredFiles.filter(file => file !== 'attestation_effectifs_chercheurs' && file !== 'preuve_charge_horaire' && file !== 'pv_attribution_charge');
    }

    return requiredFiles.filter(file => !transfert[file]);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'draft': { label: 'Brouillon', color: '#95a5a6' },
      'pending': { label: 'En attente', color: '#f39c12' },
      'approved': { label: 'Approuvé', color: '#27ae60' },
      'rejected': { label: 'Rejeté', color: '#e74c3c' },
      'submitted': { label: 'Soumis', color: '#3498db' },
      'en_cours': { label: 'En cours', color: '#f39c12' },
      'approuve': { label: 'Approuvé', color: '#27ae60' },
      'rejete': { label: 'Rejeté', color: '#e74c3c' },
      'traite': { label: 'Traité', color: '#3498db' },
    };
    return statusMap[status] || { label: status || 'Inconnu', color: '#95a5a6' };
  };

  const getTransfertStatus = (transfert) => transfert?.status || transfert?.statut || 'en_cours';

  const getTransfertName = (transfert) => (
    transfert?.nom_complet ||
    [transfert?.nom, transfert?.prenom, transfert?.postnom].filter(Boolean).join(' ') ||
    [user?.nom, user?.prenom, user?.postnom].filter(Boolean).join(' ') ||
    'Mon dossier'
  );

  const fileLabels = {
    'piece_identite': "Pièce d'identité",
    'carte_service': 'Carte de service',
    'lettre_transfert': 'Lettre de transfert',
    'formulaire_transfert_signe': 'Formulaire Ad-Hoc signé',
    'pv_origine': 'PV de provenance',
    'pv_accueil': 'PV accueil',
    'cadre_organique_accueil': 'Cadre organique',
    'attestation_effectifs_enseignants': 'Attestation enseignants',
    // 'preuve_paiement': 'Preuve de paiement Bancaire',
    'preuve_charge_horaire': 'Charge Horaire',
    'pv_attribution_charge': 'PV attribution',
    'acte_nomination': 'Acte de nomination',
    'acte_affectation': "Acte d'affectation",
    'attestation_pato': 'Attestation PATO',
    'acte_mariage': 'Acte de mariage',
    'preuve_emploi': "Preuve d'emploi",
    'coordonnees_etablissement_transfert': "Coordonnées de l'établissement de transfert",
    'preuve_nouvelle_residence': 'Preuve de nouvelle résidence',
    'preuve_activite_ou_traitement': 'Preuve activité ou traitement',
  };

  // Descriptions de 58 à 60 caractères pour chaque document
  const fileDescriptions = {
    piece_identite: "Pièce d’identité valide (carte d’électeur, passeport, permis de conduire biométrique de la RDC) du requérant",
    carte_service: "Carte professionnelle valide délivrée par l'établissement de provenance.",
    lettre_transfert: "Lettre manuscrite motivant la demande de transfert d'établissement.",
    formulaire_transfert_signe: "Un formulaire Ad-Hoc dûment rempli et signé par le requérant et les établissement/services spécialisés/centre ou institut de recherche scientifique de provenance et d’accueil, par lequel ces derniers marquent leur accord au transfert",
    pv_origine: "Procès-verbal de la réunion au cours de laquelle l’assentiment au transfert a été marqué par l’établissement/service spécialisé/centre ou institut de recherche scientifique d’origine",
    pv_accueil: "Procès-verbal de la réunion au cours de laquelle l’assentiment au transfert a été marqué par l’établissement/service spécialisé/centre ou institut de recherche scientifique d’accueil",
    cadre_organique_accueil: "Copie du cadre organique de l’établissement/service spécialisé/centre ou institut de recherche scientifique d’accueil, en cas d’existence d’un tel cadre.",
    attestation_effectifs_enseignants: "Attestation, par laquelle l’établissement/service spécialisé/centre ou institut de recherche scientifique d’accueil indique l’effectif total de son PATO et du PATO du service où il entend affecter le requérant.",

    // preuve_paiement: "La preuve du versement par le requérant de la somme équivalente à 50 USD (cinquante dollars américains), dans l'un des comptes bancaires du Ministère de l'Enseignement Supérieur, Universitaire, Recherche Scientifique et Innovations repris ci-dessous. La preuve de paiement doit impérativement porter le libellé « Paiement frais administratifs de transfert ».",
    preuve_charge_horaire: "Document prouvant la charge horaire de l'étudiant dans l'établissement.",
    pv_attribution_charge: "Procès-verbal d'attribution de charge d'enseignement ou de recherche.",
    acte_nomination: "Document officiel attestant la nomination du requérant à son poste actuel.",
    acte_affectation: "Document officiel attestant l'affectation du requérant à son établissement actuel.",
    attestation_pato: "Attestation du personnel administratif, technique et ouvrier (optionnel).",
  };

  const getUploadedFiles = (transfert) => {
    const knownFiles = Object.entries(fileLabels).filter(([key]) => transfert[key]).map(([key, label]) => ({
      key,
      label,
      url: transfert[key],
      description: fileDescriptions[key]
    }));

    const extraFiles = Object.entries(transfert)
      .filter(([key, value]) => !fileLabels[key] && isFileValue(key, value))
      .map(([key, value]) => ({
        key,
        label: formatFieldLabel(key),
        url: value,
        description: '',
      }));

    return [...knownFiles, ...extraFiles];
  };

  const getCompletionInfo = (transfert) => {
    if (isOfficeTransfert(transfert)) {
      const completed = officeFileKeys.filter(file => Boolean(transfert[file])).length;
      const total = 9;
      const percentage = Math.round((completed / total) * 100);
      return {
        completed,
        total,
        percentage,
        missingFiles: getMissingFiles(transfert),
        progressText: `${completed} / ${total} fichiers fournis`,
      };
    }

    const missingFiles = getMissingFiles(transfert);
    const expectedTotal = getMissingFiles({}).length;
    const completed = Math.max(expectedTotal - missingFiles.length, 0);
    const percentage = expectedTotal > 0 ? Math.round((completed / expectedTotal) * 100) : 0;
    return {
      completed,
      total: expectedTotal,
      percentage,
      missingFiles,
      progressText: `${completed} document(s) sur ${expectedTotal} reçu(s)`,
    };
  };

  const getAdminValueRows = (record) => {
    const source = record?.transfert || {};
    const excludedKeys = new Set([
      ...Object.keys(fileLabels),
      'id',
      'candidat',
      'candidate',
      'user',
      'transfert',
      'dossier',
      'demande',
      'transferts',
      'status',
      'statut',
      'statut_display',
      'est_admin_transfert',
      'date_creation',
      'date_modification',
      'date_demande',
      'raw_demande',
      'demande_id',
      'type_demande',
      'categorie',
      'form_type',
      'is_office_transfer',
      'numero_demande',
    ]);

    return Object.entries(source)
      .filter(([key, value]) => (
        !excludedKeys.has(key) &&
        !isUrlValue(value) &&
        !isFileValue(key, value) &&
        value !== null &&
        value !== undefined &&
        value !== '' &&
        typeof value !== 'object'
      ))
      .map(([key, value]) => ({
        key,
        label: formatFieldLabel(key),
        value: isDateLikeValue(key, value) ? formatDateValue(value) : formatDisplayValue(key, value),
      }));
  };

  const getAdminCandidateRows = (record) => {
    const candidate = record?.candidate || {};
    const candidateRows = [
      ['Nom', record?.candidateName],
      ['Email', candidate.email || record?.transfert?.email],
      ['Téléphone', candidate.telephone || record?.transfert?.telephone],
    ];

    return candidateRows
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([label, value]) => ({ label, value: String(value) }));
  };

  const handleContactCandidate = (record) => {
    const candidateEmail = (record?.candidate?.email || record?.transfert?.email || '').trim();

    if (!candidateEmail) {
      setMessage({
        type: 'error',
        text: "Adresse e-mail du candidat introuvable.",
      });
      return;
    }

    const candidateName = record?.candidateName || '';
    const subject = encodeURIComponent(`Dossier #${record?.transfertId || ''}`);
    const body = encodeURIComponent(
      `Bonjour${candidateName ? ` ${candidateName}` : ''},\n\n`
    );

    window.location.href = `mailto:${encodeURIComponent(candidateEmail)}?subject=${subject}&body=${body}`;
  };

  const handleAdminStatusChange = async (record, statut) => {
    if (!record?.statusChangeId || !statut) return;

    setChangingStatus(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/transfert/${record.statusChangeId}/change_status/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ statut }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.detail || payload?.message || 'Impossible de changer le statut');
      }

      setAdminCandidates((current) => current.map((candidateRecord) => {
        if (candidateRecord.id !== record.id) return candidateRecord;
        const updatedTransfert = { ...candidateRecord.transfert, statut };
        return { ...candidateRecord, transfert: updatedTransfert };
      }));
      setSelectedAdminRecord((current) => current?.id === record.id
        ? { ...current, transfert: { ...current.transfert, statut } }
        : current);
      setMessage({ type: 'success', text: payload?.detail || 'Statut modifié avec succès.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setChangingStatus(false);
    }
  };

  const getProvinceDetails = (transfert) => {
    const provinceDetails = [];

    if (transfert.province_origine) {
      provinceDetails.push({
        key: 'origine',
        label: "Province de provenance",
        value: transfert.province_origine,
        description: "Nom de la province de l’établissement/service spécialisé/centre ou institut de recherche scientifique de provenance",
      });
    }

    if (transfert.province_accueil) {
      provinceDetails.push({
        key: 'accueil',
        label: "Province d'accueil",
        value: transfert.province_accueil,
        description: "Nom de la province de l’établissement/service spécialisé/centre ou institut de recherche scientifique d’accueil",
      });
    }

    return provinceDetails;
  };

  const fetchOfficeTransfert = async (numeroSaisie) => {
    if (!numeroSaisie.trim()) {
      setMessage({
        type: 'error',
        text: 'Veuillez saisir un numéro de demande valide.',
      });
      return;
    }

    setLoadingOffice(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/transfert-office/${numeroSaisie.toUpperCase()}/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOfficeData(data);
      } else {
        let errorMessage = 'Transfert d\'office non trouvé';
        try {
          const error = await response.json();
          errorMessage = error.message || error.detail || errorMessage;
        } catch (e) {
          // Ignorer si le body n'est pas JSON
        }
        setMessage({
          type: 'error',
          text: `Erreur: ${errorMessage}`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Erreur réseau: ${error.message}`,
      });
    } finally {
      setLoadingOffice(false);
    }
  };

  const handleOfficeSubmit = () => {
    fetchOfficeTransfert(officeNumero);
  };

  const handleCloseOfficeModal = () => {
    setShowOfficeModal(false);
    setOfficeData(null);
    setOfficeNumero('');
  };

  const handleEditOfficeTransfert = () => {
    if (officeData) {
      onEditTransfert(officeData.numero_demande, {
        ...officeData,
        form_type: 'conjoint',
        is_office_transfer: true
      });
    }
  };

  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) return;
    
    // Si c'est une URL complète, l'ouvrir directement
    if (fileUrl.startsWith('http')) {
      window.open(fileUrl, '_blank');
    } else {
      // Sinon, l'ouvrir comme un blob
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="transfert-list-container">
        <div className="loading">{isAdmin ? 'Chargement des candidats...' : 'Chargement des transferts...'}</div>
      </div>
    );
  }

  if (isAdmin) {
    const totalPages = Math.max(Math.ceil(adminCount / adminPageSize), 1);
    const selectedStatus = selectedAdminRecord?.transfert?.statut || selectedAdminRecord?.transfert?.status || 'en_cours';
    const selectedStatusInfo = getStatusLabel(selectedStatus);
    const selectedFiles = selectedAdminRecord ? getUploadedFiles(selectedAdminRecord.transfert) : [];
    const selectedCompletion = selectedAdminRecord ? getCompletionInfo(selectedAdminRecord.transfert) : null;
    const selectedRows = selectedAdminRecord ? getAdminValueRows(selectedAdminRecord) : [];
    const selectedCandidateRows = selectedAdminRecord ? getAdminCandidateRows(selectedAdminRecord) : [];

    return (
      <>
        <div className="form-header">
          <div className="header-content">
            <div className="header-left">
              <div className="header-logo">
                <img src="/app-logo.png" alt="Logo" className="logo" />
              </div>
            </div>

            <div className="header-center">
              <h1>Candidats et dossiers</h1>
              <p className="subtitle">Consultez les demandes de transfert soumises</p>
            </div>

            <div className="header-right">
              <span className="user-info">{user?.nom} {user?.prenom}</span>
              {canAccessChat && (
                <button className="guide-btn chat-header-btn" onClick={onOpenChat}>
                  <span className="chat-button-content">
                    <FiMessageCircle size={16} /> Messagerie
                  </span>
                  {chatUnreadCount > 0 && (
                    <span className="chat-notification-badge" aria-label={`${chatUnreadCount} message(s) non lu(s)`}>
                      {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                    </span>
                  )}
                </button>
              )}
              <button className="logout-btn" onClick={onLogout}>
                Déconnexion
              </button>
            </div>
          </div>
        </div>

        <div className="transfert-list-container admin-transfert-container">
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="admin-toolbar">
            <div className="admin-search-box">
              <FiSearch size={18} />
              <input
                type="search"
                value={adminSearch}
                onChange={(event) => {
                  setAdminSearch(event.target.value);
                  setAdminPage(1);
                }}
                placeholder="Rechercher un candidat"
              />
            </div>
            <select
              className="admin-filter-select"
              value={adminStatusFilter}
              onChange={handleAdminFilterChange(setAdminStatusFilter)}
            >
              <option value="">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="approuve">Approuvé</option>
              <option value="rejete">Rejeté</option>
              <option value="traite">Traité</option>
            </select>
            <select
              className="admin-filter-select"
              value={adminCategoryFilter}
              onChange={handleAdminFilterChange(setAdminCategoryFilter)}
            >
              <option value="">Toutes catégories</option>
              {/* <option value="pato">PATO</option>
              <option value="chercheurs">Chercheurs</option>
              <option value="enseignants">Enseignants</option>
              <option value="conjoint">Conjoint(e)</option>*/}
              <option value="office">Transfert d'office</option> 
            </select>
            <select
              className="admin-filter-select"
              value={adminTypeFilter}
              onChange={handleAdminFilterChange(setAdminTypeFilter)}
            >
              <option value="">Tous types</option>
              <option value="transfert">Transfert</option>
              <option value="transfert_office">Transfert d'office</option>
            </select>
            <select
              className="admin-filter-select"
              value={adminOrdering}
              onChange={handleAdminFilterChange(setAdminOrdering)}
            >
              <option value="-date">Plus récent</option>
              <option value="date">Plus ancien</option>
            </select>
            <select
              className="admin-filter-select"
              value={adminDateFilter}
              onChange={handleAdminFilterChange(setAdminDateFilter)}
            >
              <option value="">Toutes dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="yesterday">Hier</option>
              <option value="this_week">Cette semaine</option>
              <option value="last_week">La semaine passée</option>
              <option value="this_month">Ce mois-ci</option>
              <option value="last_month">Le mois passé</option>
              <option value="custom">Période personnalisée</option>
            </select>
            <button className="admin-refresh-btn" onClick={fetchAdminCandidates}>
              <FiRefreshCw size={17} /> Actualiser
            </button>
          </div>

          <div className="admin-advanced-filters">
            {adminDateFilter === 'custom' && (
              <>
                <input
                  type="date"
                  value={adminDateStart}
                  onChange={handleAdminFilterChange(setAdminDateStart)}
                  aria-label="Date début"
                />
                <input
                  type="date"
                  value={adminDateEnd}
                  onChange={handleAdminFilterChange(setAdminDateEnd)}
                  aria-label="Date fin"
                />
              </>
            )}
          </div>

          <div className="transferts-header admin-summary-header">
            <div className="header-left">
              <h2>Candidats</h2>
            </div>
            <div className="admin-pagination">
              <button
                className="admin-page-btn"
                onClick={() => setAdminPage((page) => Math.max(page - 1, 1))}
                disabled={adminPage <= 1}
              >
                Précédent
              </button>
              <span>Page {adminPage} / {totalPages}</span>
              <button
                className="admin-page-btn"
                onClick={() => setAdminPage((page) => Math.min(page + 1, totalPages))}
                disabled={adminPage >= totalPages}
              >
                Suivant
              </button>
            </div>
          </div>

          {adminCandidates.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <FiFileText size={48} color="#007BFF" />
              </div>
              <h2>Aucun candidat trouvé</h2>
              <p>Aucun dossier ne correspond aux critères sélectionnés.</p>
            </div>
          ) : (
            <div className="transferts-layout admin-transferts-layout">
              <div className="transferts-list-column">
                {adminCandidates.map((record, idx) => {
                  const status = record.transfert?.statut || record.transfert?.status || 'en_cours';
                  const statusInfo = getStatusLabel(status);
                  const isSelected = selectedAdminRecord?.id === record.id;

                  return (
                    <div
                      key={record.id}
                      className={`transfert-list-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedAdminRecord(record)}
                    >
                      <div className="list-item-number">{String(((adminPage - 1) * adminPageSize) + idx + 1).padStart(2, '0')}</div>
                      <div className="list-item-content">
                        <div className="list-item-name">{record.candidateName}</div>
                        <div className="list-item-meta">
                          <span className="status-badge-small" style={{ backgroundColor: statusInfo.color }}>
                            {statusInfo.label}
                          </span>
                          <span className="list-item-incomplete">Dossier #{record.transfertId}</span>
                          <span className="list-item-incomplete">Candidat: {record.candidateName}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="transfert-details-column">
                {selectedAdminRecord ? (
                  <div className="transfert-details">
                    <div className="details-header">
                      <div className="details-header-top">
                        <div className="details-number">Dossier #{selectedAdminRecord.transfertId}</div>
                        <div className="details-header-actions">
                          <button
                            type="button"
                            className="contact-candidate-btn"
                            onClick={() => handleContactCandidate(selectedAdminRecord)}
                          >
                            <FiMail size={16} />
                            Contacter le candidat
                          </button>
                          <span className="status-badge" style={{ backgroundColor: selectedStatusInfo.color }}>
                            {selectedStatusInfo.label}
                          </span>
                        </div>
                      </div>
                      <h3 className="details-name">{selectedAdminRecord.candidateName}</h3>
                      <div className="details-contact">
                        <div className="contact-item">
                          <FiMail className="contact-icon" size={18} />
                          <span>{selectedAdminRecord.candidate?.email || selectedAdminRecord.transfert?.email || '—'}</span>
                        </div>
                        <div className="contact-item">
                          <FiPhone className="contact-icon" size={18} />
                          <span>{selectedAdminRecord.candidate?.telephone || selectedAdminRecord.transfert?.telephone || '—'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="details-body">
                      <div className="admin-progress-card">
                        <div className="admin-progress-top">
                          <strong>Progression du dossier</strong>
                          <span>{selectedCompletion.percentage}%</span>
                        </div>
                        <div className="admin-progress-track">
                          <div className="admin-progress-fill" style={{ width: `${selectedCompletion.percentage}%` }} />
                        </div>
                        <p>{selectedCompletion.progressText}</p>
                      </div>

                      <div className="details-grid">
                        {selectedCandidateRows.length > 0 && (
                          <div className="details-card admin-values-card">
                            <div className="card-content">
                              <h4 className="card-title">Candidat</h4>
                              <div className="admin-values-grid">
                                {selectedCandidateRows.map((row) => (
                                  <div key={row.label} className="admin-value-item">
                                    <span>{row.label}</span>
                                    <strong>{row.value}</strong>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="details-card admin-status-card">
                          <div className="card-content">
                            <h4 className="card-title">Statut du dossier</h4>
                            <select
                              className="admin-status-select"
                              value={selectedStatus}
                              onChange={(event) => handleAdminStatusChange(selectedAdminRecord, event.target.value)}
                              disabled={changingStatus}
                            >
                              <option value="en_cours">En cours</option>
                              <option value="approuve">Approuvé</option>
                              <option value="rejete">Rejeté</option>
                              <option value="traite">Traité</option>
                            </select>
                          </div>
                        </div>

                        {selectedRows.length > 0 && (
                          <div className="details-card admin-values-card">
                            <div className="card-content">
                              <h4 className="card-title">Valeurs saisies <span className="badge-count">{selectedRows.length}</span></h4>
                              <div className="admin-values-grid">
                                {selectedRows.map((row) => (
                                  <div key={row.key} className="admin-value-item">
                                    <span>{row.label}</span>
                                    <strong>{row.value}</strong>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedFiles.length > 0 && (
                          <div className="details-card files-card">
                            <div className="card-icon files-icon">
                              <FiPaperclip size={24} />
                            </div>
                            <div className="card-content">
                              <h4 className="card-title">
                                Fichiers envoyés <span className="badge-count">{selectedFiles.length}</span>
                              </h4>
                              <div className="files-compact-list">
                                {selectedFiles.map((file) => (
                                  <div key={file.key} className="file-compact-item">
                                    <span className="file-compact-label">{file.label}</span>
                                    <button
                                      className="download-icon-btn"
                                      onClick={() => handleDownloadFile(file.url, file.label)}
                                      title="Télécharger"
                                    >
                                      <FiDownload size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedCompletion.missingFiles.length > 0 && (
                          <div className="details-card alert-card">
                            <div className="card-icon warning-icon">
                              <FiAlertTriangle size={24} />
                            </div>
                            <div className="card-content">
                              <h4 className="card-title">Documents manquants</h4>
                              <div className="missing-chips">
                                {selectedCompletion.missingFiles.map((file) => (
                                  <span key={file} className="missing-chip">
                                    {fileLabels[file] || file.replace(/_/g, ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-selection">
                    <FiFileText size={48} color="#ccc" />
                    <p>Sélectionnez un candidat pour voir le dossier</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="form-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo">
              <img src="/app-logo.png" alt="Logo" className="logo" />
            </div>
          </div>

          <div className="header-center">
            <h1>Mes demandes de transfert</h1>
            <p className="subtitle">Consultez et gérez vos demandes de transfert</p>
          </div>

          <div className="header-right">
            <span className="user-info">{user?.nom} {user?.prenom}</span>
            <button className="guide-btn" onClick={onShowGuide}>
              Guide
            </button>
            {canAccessChat && (
              <button className="guide-btn chat-header-btn" onClick={onOpenChat}>
                <span className="chat-button-content">
                  <FiMessageCircle size={16} /> Messagerie
                </span>
                {chatUnreadCount > 0 && (
                  <span className="chat-notification-badge" aria-label={`${chatUnreadCount} message(s) non lu(s)`}>
                    {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                  </span>
                )}
              </button>
            )}
            <button className="logout-btn" onClick={onLogout}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="transfert-list-container">
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {transferts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FiFileText size={48} color="#007BFF" />
            </div>
            <h2>Aucune demande de transfert</h2>
            <p>Vous n'avez pas encore créé de demande de transfert.</p>
            <button className="primary-btn" onClick={onCreateNew}>
              Créer une demande de transfert
            </button>
          </div>
        ) : (
          <>
            <div className="transferts-header">
              <div className="header-left">
                <h2>Mes transferts</h2>
              </div>
              <div className="header-buttons">
                {/* <button className="primary-btn office-btn" onClick={() => setShowOfficeModal(true)}>
                  <FiClipboard size={18} /> Transfert d'office
                </button> */}
                <button className="primary-btn" onClick={onCreateNew}>
                  <FiPlus size={18} /> Nouvelle demande
                </button>
              </div>
            </div>

            <div className="transferts-layout">
              <div className="transferts-list-column">
                {transferts.map((transfert, idx) => {
                  const missingFiles = getMissingFiles(transfert);
                  const statusInfo = getStatusLabel(getTransfertStatus(transfert));
                  const isIncomplete = missingFiles.length > 0;
                  const transferNumber = String(idx + 1).padStart(2, '0');
                  const isSelected = selectedTransfert?.list_id === transfert.list_id;

                  return (
                    <div 
                      key={transfert.list_id || transfert.id} 
                      className={`transfert-list-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedTransfert(transfert)}
                    >
                      <div className="list-item-number">{transferNumber}</div>
                      <div className="list-item-content">
                        <div className="list-item-name">
                          {getTransfertName(transfert)}
                        </div>
                        <div className="list-item-meta">
                          <span className="status-badge-small" style={{ backgroundColor: statusInfo.color }}>
                            {statusInfo.label}
                          </span>
                          {isIncomplete && (
                            <span className="list-item-incomplete">
                              <span className="warning-dot">●</span> {missingFiles.length} manquant(s)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="transfert-details-column">
                {selectedTransfert ? (
                  (() => {
                    const missingFiles = getMissingFiles(selectedTransfert);
                    const statusInfo = getStatusLabel(getTransfertStatus(selectedTransfert));
                    const isIncomplete = missingFiles.length > 0;
                    const uploadedFiles = getUploadedFiles(selectedTransfert);
                    const provinceDetails = getProvinceDetails(selectedTransfert);
                    const hasProvinceInfo = provinceDetails.length > 0;
                    const transferNumber = String(transferts.findIndex(t => t.list_id === selectedTransfert.list_id) + 1).padStart(2, '0');
                    const emailValue = selectedTransfert.email || user?.email || '—';
                    const phoneValue = selectedTransfert.telephone || user?.telephone || '—';

                    return (
                      <div className="transfert-details">
                        <div className="details-header">
                          <div className="details-header-top">
                            <div className="details-number">N° {transferNumber}</div>
                            <span className="status-badge" style={{ backgroundColor: statusInfo.color }}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <h3 className="details-name">
                            {getTransfertName(selectedTransfert)}
                          </h3>
                          <div className="details-contact">
                            <div className="contact-item">
                              <FiMail className="contact-icon" size={18} />
                              <span>{emailValue}</span>
                            </div>
                            <div className="contact-item">
                              <FiPhone className="contact-icon" size={18} />
                              <span>{phoneValue}</span>
                            </div>
                          </div>
                        </div>

                        <div className="details-body">
                          <div className="details-grid">
                            {isIncomplete && (
                              <div className="details-card alert-card">
                                <div className="card-icon warning-icon">
                                  <FiAlertTriangle size={24} />
                                </div>
                                <div className="card-content">
                                  <h4 className="card-title">Documents manquants</h4>
                                  <div className="missing-chips">
                                    {missingFiles.map((file) => (
                                      <span key={file} className="missing-chip">
                                        {fileLabels[file] || file.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {uploadedFiles.length > 0 && (
                              <div className="details-card files-card">
                                <div className="card-icon files-icon">
                                  <FiPaperclip size={24} />
                                </div>
                                <div className="card-content">
                                  <h4 className="card-title">
                                    Fichiers uploadés <span className="badge-count">{uploadedFiles.length}</span>
                                  </h4>
                                  <div className="files-compact-list">
                                    {uploadedFiles.map((file) => (
                                      <div key={file.key} className="file-compact-item">
                                        <span className="file-compact-label">{file.label}</span>
                                        <button
                                          className="download-icon-btn"
                                          onClick={() => handleDownloadFile(file.url, file.label)}
                                          title="Télécharger"
                                        >
                                          <FiDownload size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {hasProvinceInfo && (
                              <div className="details-card province-card">
                                <div className="card-icon province-icon">
                                  <FiMap size={24} />
                                </div>
                                <div className="card-content">
                                  <h4 className="card-title">
                                    Provinces <span className="badge-count">{provinceDetails.length}</span>
                                  </h4>
                                  <div className="province-chips">
                                    {provinceDetails.map((province) => (
                                      <span key={province.key} className="province-chip">
                                        <strong>{province.label}:</strong> {province.value}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="details-actions">
                          {isIncomplete ? (
                            <button
                              className="secondary-btn complete-btn"
                              onClick={() => onEditTransfert(selectedTransfert.id, selectedTransfert)}
                            >
                              Compléter le dossier
                            </button>
                          ) : (
                            <button
                              className="secondary-btn"
                              onClick={() => onEditTransfert(selectedTransfert.id, selectedTransfert)}
                            >
                              Modifier
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="no-selection">
                    <FiFileText size={48} color="#ccc" />
                    <p>Sélectionnez un transfert pour voir les détails</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        
        {showOfficeModal && (
          <div className="modal-overlay" onClick={handleCloseOfficeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{officeData ? 'Détails du transfert d\'office' : 'Transfert d\'office'}</h2>
                <button 
                  className="modal-close-btn" 
                  onClick={handleCloseOfficeModal}
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="modal-body">
                {!officeData ? (
                  <>
                    <p className="modal-description">
                      Veuillez saisir votre numéro de demande de transfert d'office (ex: TXXXXXXXXXX)
                    </p>
                    
                    <div className="modal-form-group">
                      <label htmlFor="office-numero">Numéro de demande *</label>
                      <input
                        type="text"
                        id="office-numero"
                        placeholder="TXXXXXXXXXX"
                        value={officeNumero}
                        onChange={(e) => setOfficeNumero(e.target.value.toUpperCase())}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleOfficeSubmit();
                          }
                        }}
                        disabled={loadingOffice}
                      />
                    </div>
                  </>
                ) : (
                  <div className="office-results">
                    <div className="result-section">
                      <h3>Informations de la demande</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <span className="result-label">Numéro de demande:</span>
                          <span className="result-value">{officeData.numero_demande}</span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">Matricule:</span>
                          <span className="result-value">{officeData.matricule}</span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">Email:</span>
                          <span className="result-value">{officeData.email}</span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">Téléphone:</span>
                          <span className="result-value">{officeData.telephone}</span>
                        </div>
                      </div>
                    </div>

                    <div className="result-section">
                      <h3>Statut</h3>
                      <div className="result-status">
                        <span className="status-badge" style={{ backgroundColor: getStatusLabel(officeData.statut).color }}>
                          {officeData.statut_display || getStatusLabel(officeData.statut).label}
                        </span>
                      </div>
                    </div>

                    {officeData.commentaires && (
                      <div className="result-section">
                        <h3>Commentaires</h3>
                        <p className="result-comments">{officeData.commentaires}</p>
                      </div>
                    )}

                    <div className="result-section">
                      <h3>Documents</h3>
                      <div className="result-files">
                        {Object.entries({
                          piece_identite: 'Pièce d\'identité',
                          carte_service: 'Carte de service',
                          acte_affectation: 'Acte d\'affectation',
                          acte_nomination: 'Acte de nomination',
                          acte_mariage: 'Acte de mariage',
                          preuve_emploi: 'Preuve d\'emploi',
                          coordonnees_etablissement_transfert: 'Coordonnées établissement',
                          preuve_nouvelle_residence: 'Preuve nouvelle résidence',
                          preuve_activite_ou_traitement: 'Preuve activité/traitement'
                        }).map(([key, label]) => (
                          <div key={key} className="file-result-item">
                            <span className={officeData[key] ? 'file-ok' : 'file-missing'}>
                              {officeData[key] ? '✓' : '✗'} {label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <button 
                  className="secondary-btn" 
                  onClick={handleCloseOfficeModal}
                  disabled={loadingOffice}
                >
                  {officeData ? 'Fermer' : 'Annuler'}
                </button>
                {!officeData ? (
                  <button 
                    className="primary-btn" 
                    onClick={handleOfficeSubmit}
                    disabled={loadingOffice || !officeNumero.trim()}
                  >
                    {loadingOffice ? 'Chargement...' : 'Continuer'}
                  </button>
                ) : (
                  <button 
                    className="primary-btn" 
                    onClick={handleEditOfficeTransfert}
                  >
                    Modifier le dossier
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TransfertList;
